"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { ABOUT_GIRMITIYA, mediaPath } from "@/lib/media";
const MOBILE_MQ = "(max-width: 1023px)";
const STEP_SCROLL_LOCK_MS = 1100;
/** Must be near the true top/bottom of the text panel (px) */
const EDGE_THRESHOLD_PX = 32;
/** Accumulated wheel/touch overscroll before advancing a step */
const CHAIN_INTENT_SCROLLABLE = 110;
const CHAIN_INTENT_SHORT = 150;
const NAV_HEIGHTS = {
  expanded: 106,
  collapsed: 72
};
const sections = [{
  id: "about-girmitiya-meaning",
  step: "2.1",
  tag: "Origins",
  title: "Meaning and History of 'Girmitiya'",
  image: ABOUT_GIRMITIYA.meaningAndHistory.src,
  imageAlt: ABOUT_GIRMITIYA.meaningAndHistory.alt,
  paragraphs: ["The word Girmitiya derives from the English word agreement, locally pronounced as girmit by Indian labourers who could not easily articulate the foreign term. Anyone who signed a girmit, an indenture contract, came to be known as a Girmitiya.", "Historically, the term refers to the hundreds of thousands of Indian men, women, and children who were recruited, often under false pretences, to work on colonial plantations between 1834 and 1920. They replaced enslaved Africans after the abolition of slavery across the British Empire.", "The Girmitiya era left a profound mark on global history, giving rise to large Indian-origin communities in Mauritius, Fiji, Trinidad, Guyana, Suriname, South Africa, and beyond."]
}, {
  id: "about-girmitiya-indenture",
  step: "2.2",
  tag: "System",
  title: "The Indenture System",
  image: ABOUT_GIRMITIYA.indentureSystem.src,
  imageAlt: ABOUT_GIRMITIYA.indentureSystem.alt,
  paragraphs: ["The indenture system was a form of bonded labour introduced by the British colonial government in 1834 as a replacement for slavery. Under this system, Indian labourers signed a contract, the indenture, binding them to work for a specific employer in a specific colony for a fixed period, typically five years.", "In exchange, they were promised wages, accommodation, food, and the right to return passage to India after completing two terms (ten years). In practice, these promises were frequently broken. Workers faced harsh plantation conditions, low or withheld wages, physical punishment, and restricted movement.", "The system operated across British, Dutch, and French colonies. It was formally abolished in 1920 following sustained advocacy by Indian leaders including Mahatma Gandhi and C.F. Andrews."]
}, {
  id: "about-girmitiya-migration",
  step: "2.3",
  tag: "Migration",
  title: "Causes of Migration from India",
  image: ABOUT_GIRMITIYA.causesOfMigration.src,
  imageAlt: ABOUT_GIRMITIYA.causesOfMigration.alt,
  paragraphs: ["Migration under the indenture system was rarely voluntary in the truest sense. Several push and pull factors drove recruitment.", "Push factors included widespread poverty and famine in rural India (particularly Bihar, eastern Uttar Pradesh, Madras, and Bengal), caste oppression, landlessness, and the collapse of local cottage industries following British trade policies.", "Pull factors, often fabricated by recruiters known as arkattis, included promises of high wages, land ownership, and a prosperous new life abroad. Many recruits were misled about the destination, the nature of work, and contract terms.", "Women were also recruited, sometimes widows, those fleeing difficult marriages, or women displaced by social stigma, making up roughly 40% of the indentured workforce as required by colonial regulations."]
}, {
  id: "about-girmitiya-impact",
  step: "2.4",
  tag: "Legacy",
  title: "Social, Economic, and Cultural Impact",
  image: ABOUT_GIRMITIYA.culturalImpact.src,
  imageAlt: ABOUT_GIRMITIYA.culturalImpact.alt,
  paragraphs: [{
    label: "Social impact",
    text: "The indenture experience dismantled the caste hierarchy to a significant degree. On ships and plantations, people of different castes lived and worked together, creating new social bonds. At the same time, it introduced new forms of social stratification tied to plantation hierarchies."
  }, {
    label: "Economic impact",
    text: "Girmitiya labour was central to the sugar, rubber, and cocoa economies of the colonies. After indenture, many Indians became small farmers, traders, and professionals, forming the economic backbone of countries like Mauritius, Fiji, and Trinidad."
  }, {
    label: "Cultural impact",
    text: "Girmitiyas carried and transformed their culture across oceans. Hindi, Bhojpuri, and Tamil languages evolved into distinct diaspora dialects. Festivals such as Diwali, Holi, and Hosay became embedded in national cultures. Music, cuisine, and spiritual practices blended with local traditions to create vibrant Indo-diaspora identities."
  }]
}, {
  id: "about-girmitiya-slavery",
  step: "2.5",
  tag: "Comparative Note",
  title: "Girmitiya vs. Slavery",
  image: ABOUT_GIRMITIYA.girmitiyaVsSlavery.src,
  imageAlt: ABOUT_GIRMITIYA.girmitiyaVsSlavery.alt,
  paragraphs: ["While the indenture system replaced slavery, scholars and historians have long debated how different the two systems truly were in practice.", {
    label: "Legal distinction",
    text: "Indenture was technically a contractual, time-limited arrangement. Indentured labourers were legally free persons who had signed an agreement, unlike enslaved people who were property under law."
  }, {
    label: "Practical similarities",
    text: 'In practice, indentured workers faced physical punishment, restricted freedom of movement, re-indenture under pressure, and exploitative living conditions that closely mirrored slavery. Hugh Tinker famously described the indenture system as a "new system of slavery" in his landmark 1974 study.'
  }, {
    label: "Key difference",
    text: "Indenture had a defined end date and a (theoretical) right to return. Enslaved people had no such legal rights. However, the economic and social vulnerability of Girmitiyas after their contracts expired often left them in conditions barely distinguishable from those they had supposedly escaped."
  }]
}];
const SECTION_COUNT = sections.length;
const getIndexFromProgress = progress => {
  if (progress >= 1) return SECTION_COUNT - 1;
  return Math.min(Math.floor(progress * SECTION_COUNT), SECTION_COUNT - 1);
};
const isLabeledParagraph = paragraph => typeof paragraph === "object" && paragraph !== null && "label" in paragraph;
const getContentScrollBounds = el => {
  if (!el) return {
    atTop: true,
    atBottom: true,
    canScroll: false
  };
  const {
    scrollTop,
    scrollHeight,
    clientHeight
  } = el;
  const canScroll = scrollHeight > clientHeight + 8;
  return {
    canScroll,
    atTop: scrollTop <= EDGE_THRESHOLD_PX,
    atBottom: scrollTop + clientHeight >= scrollHeight - EDGE_THRESHOLD_PX
  };
};
const Timeline = () => {
  const $ = _c(74);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const stepLockRef = useRef(false);
  const touchYRef = useRef(0);
  const edgeIntentRef = useRef(0);
  const activeIndexRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [navHeight, setNavHeight] = useState(NAV_HEIGHTS.expanded);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      target: containerRef,
      offset: ["start start", "end end"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    scrollYProgress
  } = useScroll(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = index => {
      const container = containerRef.current;
      if (!container || index < 0 || index >= SECTION_COUNT) {
        return;
      }
      const containerTop = container.getBoundingClientRect().top + window.scrollY;
      const scrollable = container.offsetHeight - window.innerHeight;
      if (scrollable <= 0) {
        return;
      }
      const progress = (index + 0.12) / SECTION_COUNT;
      window.scrollTo({
        top: containerTop + progress * scrollable,
        behavior: "smooth"
      });
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const scrollToStep = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = direction => {
      if (stepLockRef.current) {
        return false;
      }
      const next = activeIndexRef.current + direction;
      if (next < 0 || next >= SECTION_COUNT) {
        return false;
      }
      stepLockRef.current = true;
      edgeIntentRef.current = 0;
      scrollToStep(next);
      window.setTimeout(() => {
        stepLockRef.current = false;
      }, STEP_SCROLL_LOCK_MS);
      return true;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const goToAdjacentStep = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = deltaY => {
      if (!window.matchMedia(MOBILE_MQ).matches) {
        return false;
      }
      const el = contentRef.current;
      if (!el || Math.abs(deltaY) < 1) {
        return false;
      }
      const {
        canScroll,
        atTop,
        atBottom
      } = getContentScrollBounds(el);
      const goingDown = deltaY > 0;
      const atChainEdge = goingDown ? !canScroll || atBottom : !canScroll || atTop;
      if (!atChainEdge) {
        edgeIntentRef.current = 0;
        return false;
      }
      if (goingDown && edgeIntentRef.current < 0 || !goingDown && edgeIntentRef.current > 0) {
        edgeIntentRef.current = 0;
      }
      edgeIntentRef.current = edgeIntentRef.current + deltaY;
      const threshold = canScroll ? CHAIN_INTENT_SCROLLABLE : CHAIN_INTENT_SHORT;
      if (Math.abs(edgeIntentRef.current) < threshold) {
        return false;
      }
      edgeIntentRef.current = 0;
      return goToAdjacentStep(goingDown ? 1 : -1);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const tryChainScroll = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const syncNavHeight = () => {
        setNavHeight(window.scrollY > 20 ? NAV_HEIGHTS.collapsed : NAV_HEIGHTS.expanded);
      };
      syncNavHeight();
      window.addEventListener("scroll", syncNavHeight, {
        passive: true
      });
      return () => window.removeEventListener("scroll", syncNavHeight);
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== activeIndex) {
    t6 = () => {
      activeIndexRef.current = activeIndex;
    };
    t7 = [activeIndex];
    $[6] = activeIndex;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== scrollYProgress) {
    t8 = () => {
      const unsubscribe = scrollYProgress.on("change", latest => {
        setActiveIndex(getIndexFromProgress(latest));
      });
      return () => unsubscribe();
    };
    t9 = [scrollYProgress];
    $[9] = scrollYProgress;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      edgeIntentRef.current = 0;
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== activeIndex) {
    t11 = [activeIndex];
    $[13] = activeIndex;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const el_0 = contentRef.current;
      if (!el_0) {
        return;
      }
      const onWheel = e => {
        if (!window.matchMedia(MOBILE_MQ).matches) {
          return;
        }
        const chained = tryChainScroll(e.deltaY);
        if (chained) {
          e.preventDefault();
        }
      };
      const onTouchStart = e_0 => {
        touchYRef.current = e_0.touches[0]?.clientY ?? 0;
        edgeIntentRef.current = 0;
      };
      const onTouchMove = e_1 => {
        if (!window.matchMedia(MOBILE_MQ).matches) {
          return;
        }
        const y = e_1.touches[0]?.clientY ?? touchYRef.current;
        const deltaY_0 = touchYRef.current - y;
        touchYRef.current = y;
        if (Math.abs(deltaY_0) < 4) {
          return;
        }
        const chained_0 = tryChainScroll(deltaY_0);
        if (chained_0) {
          e_1.preventDefault();
        }
      };
      const onContentScroll = () => {
        const {
          atTop: atTop_0,
          atBottom: atBottom_0,
          canScroll: canScroll_0
        } = getContentScrollBounds(el_0);
        if (canScroll_0 && !atTop_0 && !atBottom_0) {
          edgeIntentRef.current = 0;
        }
      };
      el_0.addEventListener("wheel", onWheel, {
        passive: false
      });
      el_0.addEventListener("touchstart", onTouchStart, {
        passive: true
      });
      el_0.addEventListener("touchmove", onTouchMove, {
        passive: false
      });
      el_0.addEventListener("scroll", onContentScroll, {
        passive: true
      });
      return () => {
        el_0.removeEventListener("wheel", onWheel);
        el_0.removeEventListener("touchstart", onTouchStart);
        el_0.removeEventListener("touchmove", onTouchMove);
        el_0.removeEventListener("scroll", onContentScroll);
      };
    };
    t13 = [tryChainScroll];
    $[15] = t12;
    $[16] = t13;
  } else {
    t12 = $[15];
    t13 = $[16];
  }
  useEffect(t12, t13);
  const active = sections[activeIndex];
  const isDense = active.paragraphs.length >= 4;
  const t14 = `calc(100svh - ${navHeight}px)`;
  let t15;
  if ($[17] !== navHeight || $[18] !== t14) {
    t15 = {
      top: navHeight,
      height: t14
    };
    $[17] = navHeight;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  let t17;
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      opacity: 0
    };
    t17 = {
      opacity: 1
    };
    t18 = {
      opacity: 0
    };
    $[20] = t16;
    $[21] = t17;
    $[22] = t18;
  } else {
    t16 = $[20];
    t17 = $[21];
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    };
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== active.image) {
    t20 = mediaPath(active.image);
    $[24] = active.image;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  const t21 = active.imageAlt || active.title;
  let t22;
  if ($[26] !== t20 || $[27] !== t21) {
    t22 = <img src={t20} alt={t21} className="timeline-hero-img" />;
    $[26] = t20;
    $[27] = t21;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#0F1C2E]/60 via-transparent to-transparent lg:from-transparent lg:via-[#0F1C2E]/25 lg:to-[#0F1C2E]/80 pointer-events-none" />;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  let t24;
  if ($[30] !== activeIndex || $[31] !== t22) {
    t24 = <div className="hidden lg:block lg:w-1/2 lg:min-h-0 lg:flex-1 relative overflow-hidden bg-[#0F1C2E] shrink-0"><AnimatePresence mode="sync"><motion.div key={activeIndex} initial={t16} animate={t17} exit={t18} transition={t19} className="absolute inset-0">{t22}{t23}</motion.div></AnimatePresence></div>;
    $[30] = activeIndex;
    $[31] = t22;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  let t26;
  let t27;
  let t28;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      opacity: 0,
      y: 50
    };
    t26 = {
      opacity: 1,
      y: 0
    };
    t27 = {
      opacity: 0,
      y: -50
    };
    t28 = {
      duration: 0.8
    };
    $[33] = t25;
    $[34] = t26;
    $[35] = t27;
    $[36] = t28;
  } else {
    t25 = $[33];
    t26 = $[34];
    t27 = $[35];
    t28 = $[36];
  }
  let t29;
  if ($[37] !== active.step || $[38] !== activeIndex) {
    t29 = <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden opacity-[0.03]"><AnimatePresence mode="popLayout"><motion.span key={activeIndex} initial={t25} animate={t26} exit={t27} transition={t28} className="font-display text-[18vh] sm:text-[22vh] lg:text-[32vh] font-bold text-[#F4EFE6] leading-none select-none">{active.step}</motion.span></AnimatePresence></div>;
    $[37] = active.step;
    $[38] = activeIndex;
    $[39] = t29;
  } else {
    t29 = $[39];
  }
  let t30;
  let t31;
  let t32;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      opacity: 0,
      y: 24
    };
    t31 = {
      opacity: 1,
      y: 0
    };
    t32 = {
      opacity: 0,
      y: -16
    };
    $[40] = t30;
    $[41] = t31;
    $[42] = t32;
  } else {
    t30 = $[40];
    t31 = $[41];
    t32 = $[42];
  }
  let t33;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    };
    $[43] = t33;
  } else {
    t33 = $[43];
  }
  let t34;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className="flex items-center gap-2 sm:gap-3 mb-4 lg:mb-5"><div className="w-6 sm:w-8 h-px bg-[#DF6D2A]" /><span className="font-body text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-[#DF6D2A] font-light">About Girmitiya</span></div>;
    $[44] = t34;
  } else {
    t34 = $[44];
  }
  let t35;
  if ($[45] !== active.step) {
    t35 = <span className="font-display text-[32px] sm:text-[42px] lg:text-[56px] xl:text-[64px] leading-none text-[#DF6D2A] italic pr-3 sm:pr-4 border-r border-white/10">{active.step}</span>;
    $[45] = active.step;
    $[46] = t35;
  } else {
    t35 = $[46];
  }
  let t36;
  if ($[47] !== active.tag) {
    t36 = <span className="font-body text-[9px] sm:text-[10px] tracking-[0.15em] sm:tracking-[0.2em] uppercase text-[#C9A84C] border border-[#C9A84C]/30 px-2 sm:px-3 py-1 sm:py-1.5 rounded-sm">{active.tag}</span>;
    $[47] = active.tag;
    $[48] = t36;
  } else {
    t36 = $[48];
  }
  let t37;
  if ($[49] !== t35 || $[50] !== t36) {
    t37 = <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">{t35}{t36}</div>;
    $[49] = t35;
    $[50] = t36;
    $[51] = t37;
  } else {
    t37 = $[51];
  }
  let t38;
  if ($[52] !== active.title) {
    t38 = <h3 className="font-display text-[22px] sm:text-[26px] lg:text-[32px] xl:text-[36px] text-[#F4EFE6] leading-[1.15] mb-3 sm:mb-4 lg:mb-5">{active.title}</h3>;
    $[52] = active.title;
    $[53] = t38;
  } else {
    t38 = $[53];
  }
  const t39 = `flex flex-col ${isDense ? "gap-2 sm:gap-2.5" : "gap-2.5 sm:gap-3"}`;
  let t40;
  if ($[54] !== active.paragraphs || $[55] !== isDense) {
    let t41;
    if ($[57] !== isDense) {
      t41 = (paragraph, i) => isLabeledParagraph(paragraph) ? <p key={i} className={`font-body font-light leading-[1.6] sm:leading-[1.65] text-[#F4EFE6]/65 ${isDense ? "text-[12px] sm:text-[13px] lg:text-[13.5px]" : "text-[12.5px] sm:text-[13.5px] lg:text-[14.5px]"}`}><span className="text-[#F4EFE6]/85 font-normal">{paragraph.label}:</span>{" "}{paragraph.text}</p> : <p key={i} className={`font-body font-light leading-[1.6] sm:leading-[1.65] text-[#F4EFE6]/65 ${isDense ? "text-[12px] sm:text-[13px] lg:text-[13.5px]" : "text-[12.5px] sm:text-[13.5px] lg:text-[14.5px]"}`}>{paragraph}</p>;
      $[57] = isDense;
      $[58] = t41;
    } else {
      t41 = $[58];
    }
    t40 = active.paragraphs.map(t41);
    $[54] = active.paragraphs;
    $[55] = isDense;
    $[56] = t40;
  } else {
    t40 = $[56];
  }
  let t41;
  if ($[59] !== t39 || $[60] !== t40) {
    t41 = <div className={t39}>{t40}</div>;
    $[59] = t39;
    $[60] = t40;
    $[61] = t41;
  } else {
    t41 = $[61];
  }
  let t42;
  if ($[62] !== activeIndex || $[63] !== t37 || $[64] !== t38 || $[65] !== t41) {
    t42 = <div className="relative z-10 px-5 sm:px-10 md:px-16 lg:px-20 xl:px-24 pt-6 pb-10 sm:pt-8 sm:pb-12 lg:py-8"><div className="w-full max-w-[520px]"><AnimatePresence mode="wait"><motion.div key={activeIndex} initial={t30} animate={t31} exit={t32} transition={t33} className="flex flex-col items-start">{t34}{t37}{t38}{t41}</motion.div></AnimatePresence></div></div>;
    $[62] = activeIndex;
    $[63] = t37;
    $[64] = t38;
    $[65] = t41;
    $[66] = t42;
  } else {
    t42 = $[66];
  }
  let t43;
  if ($[67] !== t29 || $[68] !== t42) {
    t43 = <div ref={contentRef} className="timeline-content-scroll w-full lg:w-1/2 flex-1 min-h-0 relative flex flex-col bg-[#0F1C2E] z-10 overflow-y-auto [-webkit-overflow-scrolling:touch] lg:overflow-hidden lg:flex-1 lg:justify-center">{t29}{t42}</div>;
    $[67] = t29;
    $[68] = t42;
    $[69] = t43;
  } else {
    t43 = $[69];
  }
  let t44;
  if ($[70] !== t15 || $[71] !== t24 || $[72] !== t43) {
    t44 = <><section ref={containerRef} id="about-girmitiya" className="relative font-body bg-[#0F1C2E] h-[280vh] sm:h-[320vh] lg:h-[450vh] xl:h-[550vh]"><div className="sticky w-full flex flex-col lg:flex-row lg:items-stretch overflow-hidden timeline-grain" style={t15}>{t24}{t43}</div></section></>;
    $[70] = t15;
    $[71] = t24;
    $[72] = t43;
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  return t44;
};
export default Timeline;